import { Injectable } from '@angular/core';
import {OccEndpointsService, TranslationService, UserIdService} from '@spartacus/core';
import { HttpClient } from '@angular/common/http';
import {catchError, map, Observable, switchMap} from 'rxjs';

import { failStatus, successStatus } from '../../../../core/models/request-status.model';
import {
  Return, ReturnDetails, ReturnRequestConfig,
  ReturnsRequestConfig,
  ReturnsStatusRequest, ReturnStatusRequest,
} from "./return.model";
import {OrderEntry} from "@spartacus/cart/base/root";

@Injectable({
  providedIn: 'root',
})
export class ReturnAdapter {
  constructor(
    private http: HttpClient,
    private translationService: TranslationService,
    private occEndPointService: OccEndpointsService,
    private userIdService: UserIdService
  ) {}

  getReturns(returnsRequestConfig: ReturnsRequestConfig): Observable<ReturnsStatusRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('getReturns', {
          urlParams: {
            userId: userId
          },
          queryParams:{
            fromDate: returnsRequestConfig.fromDate,
            toDate: returnsRequestConfig.toDate,
            onlyOpen: returnsRequestConfig.onlyOpen,
            poNumber: returnsRequestConfig.poNumber,
            invoiceNumber: returnsRequestConfig.invoiceNumber,
            orderNumber: returnsRequestConfig.orderNumber,
            partNumber: returnsRequestConfig.partNumber,
          }
        });

        return this.http.get(url).pipe(
          map((response: any) => ({
            returns: this.normalizeReturns(response),
            ...successStatus,
          })),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }

  getOrder(returnRequestConfig: ReturnRequestConfig): Observable<ReturnStatusRequest> {
    const url = this.occEndPointService.buildUrl('getOrder', {
      urlParams: {
        uid: returnRequestConfig.uid,
      },
    });

    return this.http.get(url).pipe(
      map((response: any) => ({
        return: this.normalizeOrder(response),
        ...successStatus,
      })),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            ...failStatus,
            message: textError,
          })),
        );
      }),
    );
  }

  getReturnsExport(returnsRequestConfig: ReturnsRequestConfig): Observable<any> {

    const url = this.occEndPointService.buildUrl('getReturnsExport', {
      queryParams:{
        fromDate: returnsRequestConfig.fromDate,
        toDate: returnsRequestConfig.toDate,
        onlyOpen: returnsRequestConfig.onlyOpen,
        poNumber: returnsRequestConfig.poNumber,
        invoiceNumber: returnsRequestConfig.invoiceNumber,
        orderNumber: returnsRequestConfig.orderNumber,
        partNumber: returnsRequestConfig.partNumber,
      },
    });

    return this.http.get(url, { responseType: 'json' });
  }

  exportDetailsOrder(returnsRequestConfig: ReturnRequestConfig): Observable<any> {
    const url = this.occEndPointService.buildUrl('exportDetailsOrder', {
      urlParams: {
        uid: returnsRequestConfig.uid,
      },
    });

    return this.http.get(url, { responseType: 'json' });
  }

  normalizeReturns(_returns: any): Return[] {
    const returns: Return[] = [];

    (_returns || []).forEach((r: any) => {
      const catalogNumber = r?.catalogNumber ? `Catalog #: ${r?.catalogNumber}` : '';
      const partNumber = r?.partNumber ? `<br> Part #: ${r?.partNumber}` : '';
      const customerPartNumber = r?.customerPartNumber ? `<br> Customer Part #: ${r?.customerPartNumber}` : '';
      const product: string = catalogNumber.concat(partNumber).concat(customerPartNumber);

      returns.push({
        orderUid: r?.orderUid || '',
        orderNumber: r?.orderNumber || '',
        poNumber: r.poNumber || '',
        lineNumber: r?.lineNumber || '',
        product: product,
        orderDate: r?.orderDate ? new Date(r?.orderDate) : undefined,
        quantityOrdered: r?.quantityOrdered || '0',
        quantityAccepted: r?.quantityAccepted || '0',
        sellingPrice: {
          value: r?.sellingPrice?.value || 0,
          formattedValue: r?.sellingPrice?.formattedValue || '',
        },
        total: {
          value: r?.total?.value || 0,
          formattedValue: r?.total?.formattedValue || '',
        }
      });
    });

    return returns;
  }

  normalizeOrder(_order: any): ReturnDetails {
    const entries: OrderEntry[] = [];

    (_order?.lines || []).forEach((entry: any) => {
      entries.push({
        product: entry?.product,
        notes: entry?.notes || '',
        status: entry?.lineStatus || '',
        quantity: entry?.quantityOrdered || 0,
        invoice: entry?.invoiceNumber || '',
        requestedShipDate: entry.requestDate ? new Date(entry.requestDate) : undefined,
        estimatedShipDate: entry.estimatedShipDate ? new Date(entry.estimatedShipDate).toString() : undefined,
        actualShipDate: entry.actualShipDate ? new Date(entry.actualShipDate) : undefined,
        trackingInfo: entry?.trackingInfo || '',
        poLine: entry?.lineNumber || '',
        warehouse: entry?.warehouse || '',
        basePrice: {
          value: entry?.basePrice?.value || 0,
          formattedValue: entry?.basePrice?.formattedValue || '',
        },
        totalPrice: {
          value: entry?.totalPrice?.value || 0,
          formattedValue: entry?.totalPrice?.formattedValue || '',
        },
      });
    });

    const addressParts = [
      _order?.shippingAddress?.line1,
      _order?.shippingAddress?.region?.name,
      _order?.shippingAddress?.town,
      _order?.shippingAddress?.postalCode
    ];
    
    const shippingAddress = addressParts.filter(part => part).join(', ');

    return {
      returnUid: _order?.uid || '',
      orderId: _order.orderId || '',
      orderNumber: _order?.orderNumber || '',
      orderStatus: _order?.orderStatus || '',
      orderDate: _order?.orderDate || undefined,
      requestDate: _order?.requestDate || undefined,
      bookedDate: _order?.bookedDate || undefined,
      orderPricingSummary: {        
        orderSubTotal: {
          value: _order?.totalLines?.value || 0,
          formattedValue: _order?.totalLines?.formattedValue || '',
        },
        tariffSurcharge: {
          value: _order?.totalTax?.value || 0,
          formattedValue: _order?.totalTax?.formattedValue || '',
        },
        shippingHandling: {
          value: _order?.totalShipping?.value || 0,
          formattedValue: _order?.totalShipping?.formattedValue || '',
        },
        orderTotal: {
          value: _order?.total?.value || 0,
          formattedValue: _order?.total?.formattedValue || '',
        },
      },
      shippingInformation: {
        address: shippingAddress || '',
        method: _order?.shippingMethod || '',
        freightTerms: _order?.freightTerms || '',
        email: _order?.shipToAttention || ''
      },
      paymentMethod: {
        poNumber: _order?.poNumber || '',
        paymentTerms: _order?.paymentTerms || '',
        billingAddress: _order?.billingAddress?.formattedAddress || ''
      },
      lines: entries
    }
  }
}
